import { useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useQuery } from "@connectrpc/connect-query";
import { skipToken } from "@tanstack/react-query";
import { ArrowLeftIcon, KeyRoundIcon, SearchXIcon, UsersIcon } from "lucide-react";

import { ErrorState } from "@/components/error-state";
import { EmptyState, PageHeader } from "@/components/page-header";
import { AccountStatusBadge, StatusDot } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AccountStatus, AdminService } from "@/gen/wabms/admin/v1/admin_pb";
import { isNotFound } from "@/lib/errors";
import { accountStatusHint, accountStatusTone, formatBoth, formatCount } from "@/lib/format";
import { GroupsTab } from "@/routes/accounts/groups-tab";
import { SendPolicyTab } from "@/routes/accounts/send-policy-tab";
import { TokensTab } from "@/routes/accounts/tokens-tab";
import { WebhooksTab } from "@/routes/accounts/webhooks-tab";
import { useUiStore } from "@/stores/ui-store";

const TABS = ["tokens", "policy", "webhooks", "groups"] as const;
type TabValue = (typeof TABS)[number];

function isTab(value: string | null): value is TabValue {
  return value !== null && (TABS as readonly string[]).includes(value);
}

export function AccountDetailPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const setSelectedAccountId = useUiStore((s) => s.setSelectedAccountId);

  const accountQuery = useQuery(
    AdminService.method.getAccount,
    accountId ? { accountId } : skipToken,
  );
  const account = accountQuery.data?.account;

  useEffect(() => {
    if (accountId) setSelectedAccountId(accountId);
  }, [accountId, setSelectedAccountId]);

  const tabParam = searchParams.get("tab");
  const tab: TabValue = isTab(tabParam) ? tabParam : "tokens";

  // NOT_FOUND is a real answer, not a failure; anything else is.
  if (accountQuery.isError && !isNotFound(accountQuery.error)) {
    return (
      <ErrorState
        error={accountQuery.error}
        title="Could not load this account"
        onRetry={() => void accountQuery.refetch()}
      />
    );
  }

  if (accountQuery.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  if (!account) {
    return (
      <EmptyState
        icon={<SearchXIcon />}
        title="Account not found"
        description={`No account with id ${accountId ?? "(none)"}.`}
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/accounts">Back to accounts</Link>
          </Button>
        }
      />
    );
  }

  return (
    <>
      <div className="space-y-3">
        <Button asChild variant="ghost" size="sm" className="-ml-2 w-fit">
          <Link to="/accounts">
            <ArrowLeftIcon /> Accounts
          </Link>
        </Button>

        <PageHeader
          title={
            <span className="flex items-center gap-2">
              <StatusDot
                tone={accountStatusTone(account.status)}
                pulse={account.status === AccountStatus.CONNECTING}
              />
              {account.label}
            </span>
          }
          description={accountStatusHint(account.status)}
          actions={<AccountStatusBadge status={account.status} />}
        />

        <dl className="grid gap-x-8 gap-y-1 text-xs sm:grid-cols-2 lg:grid-cols-4">
          <Meta label="Account id">
            <code className="font-mono">{account.accountId}</code>
          </Meta>
          <Meta label="JID">
            <code className="font-mono break-all">{account.jid || "—"}</code>
          </Meta>
          <Meta label="Phone">{account.phoneE164 || "—"}</Meta>
          <Meta label="Push name">{account.pushName || "—"}</Meta>
          <Meta label="Paired">{account.pairedAt ? formatBoth(account.pairedAt) : "never"}</Meta>
          <Meta label="Last connected">
            {account.lastConnectedAt ? formatBoth(account.lastConnectedAt) : "never"}
          </Meta>
          <Meta label="Queue depth">{formatCount(account.queueDepth)}</Meta>
          <Meta label="Sent today">
            {formatCount(account.sentToday)} / {formatCount(account.sendPolicy?.dailyCap ?? 0)}
          </Meta>
        </dl>
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => setSearchParams({ tab: v }, { replace: true })}
        className="gap-4"
      >
        <TabsList>
          <TabsTrigger value="tokens">
            <KeyRoundIcon /> Tokens
          </TabsTrigger>
          <TabsTrigger value="policy">Send policy</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
          <TabsTrigger value="groups">
            <UsersIcon /> Groups
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tokens">
          <TokensTab account={account} />
        </TabsContent>
        <TabsContent value="policy">
          <SendPolicyTab account={account} />
        </TabsContent>
        <TabsContent value="webhooks">
          <WebhooksTab account={account} />
        </TabsContent>
        <TabsContent value="groups">
          <GroupsTab account={account} />
        </TabsContent>
      </Tabs>
    </>
  );
}

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2 border-b py-1.5 last:border-b-0 sm:border-b-0">
      <dt className="w-28 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-all">{children}</dd>
    </div>
  );
}
